/**
 * The single, app-wide connection to a USB scale.
 *
 * Deliberately a module-level singleton rather than a React provider. A station
 * has one scale on the bench, but several weight fields want to read it — the
 * pack modal's add-bar, stocktake, receive-stock — and those mount and unmount
 * constantly. Owning the connection outside React means opening the device once
 * per page load instead of once per modal, and it keeps `main.tsx`'s provider
 * stack untouched.
 *
 * Components subscribe through `useScale`, which wraps this in
 * `useSyncExternalStore`.
 */
import { bridgeSuspendDelay } from './bridge-suspend';
import { BridgeTransport } from './bridge-transport';
import { type RelayCredentials, RelayTransport } from './relay-transport';
import {
  decodeScaleReport,
  isScaleDevice,
  SCALE_DEVICE_FILTERS,
  type ScaleReading,
} from './hid-pos-scale';

/** Remembers which granted device to reopen when more than one is paired. */
const STORAGE_KEY = 'farmshare.scale.device';

/** Remembers which scale this browser weighs on, across reloads. */
const SELECTED_KEY = 'farmshare.scale.selected';

/**
 * Read the remembered selection.
 * @returns The device id last selected here, if any.
 */
function readSelected(): string | null {
  try {
    return localStorage.getItem(SELECTED_KEY);
  } catch {
    return null;
  }
}

/**
 * Choose which scale to weigh on.
 *
 * Honours the operator's remembered choice when that scale is still present,
 * and otherwise falls to the first connected one — a station whose postal scale
 * was unplugged should quietly start using the other rather than going dead.
 * @param devices - Every scale currently available.
 * @param preferred - The operator's remembered or explicit choice.
 * @returns The id to select, or null when there's nothing to weigh on.
 */
function resolveSelection(
  devices: ScaleDevice[],
  preferred: string | null,
): string | null {
  // Only ever the operator's own choice. Auto-picking the first connected scale
  // reads as helpful until a bench has two: the field starts mirroring a scale
  // nobody chose, and a weight off the wrong platter looks exactly like a
  // weight off the right one. Choosing is cheap; a mislabelled package isn't.
  return preferred && devices.some((d) => d.id === preferred)
    ? preferred
    : null;
}

export type ScaleConnectionStatus =
  | 'unsupported'
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'error';

/**
 * Where the current readings are coming from.
 *
 * In preference order: `bridge` is a station on this machine (instant, free,
 * works offline), `webhid` is a scale attached to this browser, and `relay` is
 * a station reached through the cloud — the only path to a tablet, and the only
 * one that costs anything per weight.
 */
export type ScaleSource = 'bridge' | 'webhid' | 'relay' | null;

/** One scale the operator can weigh on. */
export interface ScaleDevice {
  id: string;
  label: string;
  connected: boolean;
}

/**
 * Id used for the browser's own WebHID scale.
 *
 * A fixed value rather than a real device id: WebHID gives us one device at a
 * time and no stable identifier for it, so it occupies a single reserved slot
 * in the same list the bridge's scales appear in. That keeps every consumer —
 * the picker, the selection memory, the hook — transport-agnostic.
 */
export const WEBHID_DEVICE_ID = 'webhid';

export interface ScaleState {
  status: ScaleConnectionStatus;
  /** Every scale available right now, across transports. */
  devices: ScaleDevice[];
  /** Which scale weight fields read from. */
  selectedId: string | null;
  /** Latest reading per device — a station may have several scales live. */
  readings: Record<string, ScaleReading>;
  error: string | null;
  /** Which transport is live. Drives whether a Connect button is offered. */
  source: ScaleSource;
  /** Station name, when served by Farmshare Desktop. */
  stationName: string | null;
  /** Whether a scale has been live at some point this session. */
  everConnected: boolean;
  /**
   * True once inactivity closed the station connection.
   *
   * Surfaced rather than hidden because the operator can be standing right
   * there: a package set down on a suspended scale produces nothing, and
   * "paused, click to resume" is the difference between that and a scale that
   * looks broken.
   */
  suspended: boolean;
}

const listeners = new Set<() => void>();

/**
 * WebHID is Chromium-only, but the bridge works anywhere `ws://127.0.0.1` is
 * allowed — Firefox included. So "supported" means *either* transport could
 * work; only Safari, which has neither, hides the scale UI entirely.
 */
const hidSupported = typeof navigator !== 'undefined' && !!navigator.hid;
const bridgeSupported = typeof WebSocket !== 'undefined';
const supported = hidSupported || bridgeSupported;

let state: ScaleState = {
  status: supported ? 'idle' : 'unsupported',
  devices: [],
  selectedId: null,
  readings: {},
  error: null,
  source: null,
  stationName: null,
  suspended: false,
  everConnected: false,
};

let device: HIDDevice | null = null;

/**
 * The two device sources, kept apart and merged into `state.devices`.
 *
 * They coexist deliberately. A station can be serving scales over the bridge
 * while the operator also attaches one straight to this browser — a scale the
 * station hasn't bound, or a laptop pulled up to a second bench. The only thing
 * that can't happen is both opening the *same* physical device, and the OS
 * enforces that itself: the second `open()` throws, and we surface it.
 */
let bridgeDevices: ScaleDevice[] = [];
/**
 * Scales reached through the relay, per station. A vendor can run several
 * stations and each advertises only its own list, so replacing the whole list
 * with the latest frame made one station's scales vanish whenever another spoke.
 */
const relayStations = new Map<
  string,
  { label?: string; devices: ScaleDevice[] }
>();
let webhidDevice: ScaleDevice | null = null;

/**
 * The scale the operator explicitly attached to this browser, if any.
 *
 * Held as an identity rather than a boolean because it has to outlive the
 * device object. Scales power themselves down on standby and come back as a
 * new* `HIDDevice`, so "is something open right now" can't answer "should
 * something be open" — that distinction is what lets a power-cycled scale
 * reattach on its own instead of needing to be picked again.
 *
 * Cleared only by {@link disconnectScale}: the operator letting a scale go is a
 * decision, the hardware blinking is not.
 */
let webhidWanted: { vendorId: number; productId: number } | null = null;
/** Guards `restore()` so React strict-mode double-mounts open the device once. */
let restorePromise: Promise<void> | null = null;
let bridge: BridgeTransport | null = null;

/**
 * The cloud relay, for devices the loopback bridge can't reach.
 *
 * Last resort by design. It's the only path to a tablet, but it costs money per
 * message and depends on the shop's internet, so it's only dialled once
 * loopback has had its chance and WebHID has nothing to offer.
 */
let relay: RelayTransport | null = null;
let relayCredentials: RelayCredentials | null = null;

/** Countdown to dropping the station connection through inactivity. */
let suspendTimer: ReturnType<typeof setTimeout> | null = null;

/** True once inactivity closed the socket, so activity knows to reopen it. */
let suspended = false;

/**
 * Whether a scale has been live at any point this session.
 *
 * Draws the line between "no scale here" and "the scale went away". A laptop
 * that never had one is working normally and its weight fields should look
 * ordinary; a bench that was weighing a minute ago and now isn't needs saying,
 * because the operator will otherwise set a package down and get nothing.
 */
let everConnected = false;

/** @returns The current snapshot — stable by reference between changes. */
export function getScaleState(): ScaleState {
  return state;
}

/**
 * Subscribe to state changes.
 * @param listener - Called after every committed state change.
 * @returns An unsubscribe function.
 */
export function subscribeToScale(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Commit a patch and notify subscribers.
 * @param patch - Fields to merge into the current state.
 */
function setState(patch: Partial<ScaleState>): void {
  state = { ...state, everConnected, ...patch };
  listeners.forEach((l) => l());
}

/**
 * Identity for a paired device, so we reopen the same one next session.
 *
 * WebHID exposes no stable serial or device ID, so vendor/product/name is the
 * best key available. Two identical scales on one station would collide; that
 * resolves itself the moment the operator picks one, and is not a setup we
 * expect on a packing bench.
 * @param d - The device to key.
 * @returns A stable-enough identity string for this device.
 */
function deviceKey(d: HIDDevice): string {
  return `${d.vendorId}:${d.productId}:${d.productName}`;
}

/**
 * Handle one input report from the scale.
 *
 * Scales re-send the same reading while the platter sits still, so identical
 * readings are dropped rather than re-rendering every weight field on screen
 * several times a second.
 * @param event - The WebHID input report event.
 */
function handleInputReport(event: HIDInputReportEvent): void {
  const reading = decodeScaleReport(event.data);
  if (!reading) return;

  recordReading(WEBHID_DEVICE_ID, reading);
}

/**
 * Store a reading for one device, dropping no-op repeats.
 *
 * Scales re-send the same reading while the platter sits still, so identical
 * readings are dropped rather than re-rendering every weight field on screen
 * several times a second.
 * @param deviceId - Which scale it came from.
 * @param reading - The decoded reading.
 */
function recordReading(deviceId: string, reading: ScaleReading): void {
  const prev = state.readings[deviceId];
  if (
    prev &&
    prev.pounds === reading.pounds &&
    prev.status === reading.status &&
    prev.unitCode === reading.unitCode
  ) {
    return;
  }
  // Reaching here means the weight actually changed — somebody is using the
  // scale, which is activity even when nobody has touched the keyboard.
  markActivity();
  setState({ readings: { ...state.readings, [deviceId]: reading } });
}

/**
 * Turn a thrown value into something worth showing an operator.
 * @param err - The caught value.
 * @returns A message an operator can act on.
 */
function describeError(err: unknown): string {
  const message = err instanceof Error ? err.message : String(err);
  // The usual cause on a shared station: another tab or the OS holds the device.
  if (/already open|access denied|in use/i.test(message)) {
    return 'Scale is in use by another tab or app';
  }
  return message || 'Could not connect to the scale';
}

/** Close the current device and drop its listener. */
async function detach(): Promise<void> {
  webhidDevice = null;
  if (!device) return;
  device.removeEventListener('inputreport', handleInputReport);
  try {
    if (device.opened) await device.close();
  } catch {
    // Already gone — unplugged mid-session. Nothing to clean up.
  }
  device = null;
}

/**
 * Open a device and start listening.
 * @param next - Device to attach to.
 */
async function attach(next: HIDDevice): Promise<void> {
  if (device === next && device.opened) return;
  await detach();

  if (!next.opened) await next.open();
  next.addEventListener('inputreport', handleInputReport);
  device = next;

  try {
    localStorage.setItem(STORAGE_KEY, deviceKey(next));
  } catch {
    // Private-mode storage failures only cost us the auto-reconnect hint.
  }

  // Named to stay clear of the module-level `device`, which is the open
  // HIDDevice rather than the list entry describing it.
  webhidDevice = {
    id: WEBHID_DEVICE_ID,
    label: next.productName || 'USB scale',
    connected: true,
  };
  everConnected = true;
  stopRelay();
  setState({
    status: 'connected',
    error: null,
    // A live bridge keeps naming the transport, since that's what most of the
    // station's scales are coming from.
    source: state.source === 'bridge' ? 'bridge' : 'webhid',
  });
  publishDevices();
}

/**
 * Supply the relay with somewhere to dial and someone to be.
 *
 * Pushed in from React rather than read here: the token lives in the auth
 * context and the vendor in Recoil, neither of which this module can reach.
 * @param next - Endpoint and credentials, or null to disable the relay.
 */
export function setRelayCredentials(next: RelayCredentials | null): void {
  const changed =
    next?.url !== relayCredentials?.url ||
    next?.token !== relayCredentials?.token ||
    next?.vendorId !== relayCredentials?.vendorId;
  if (!changed) return;

  relayCredentials = next;
  // A refreshed token has to be carried into a new connection: the relay
  // authorizes at connect time only, so an open socket keeps working, but the
  // next reconnect must not present the expired one.
  if (relay) {
    relay.stop();
    relay = null;
  }
  maybeStartRelay();
}

/**
 * Dial the relay, if it's needed and possible.
 *
 * Skipped entirely while a station is on loopback or a scale is attached
 * here — both are faster, free, and work with the internet down.
 */
function maybeStartRelay(): void {
  if (relay || suspended) return;
  if (!relayCredentials?.url) return;
  if (bridgeIsLive() || webhidDevice) return;

  relay = new RelayTransport(relayCredentials, {
    onOpen: (stationName) => {
      setState({
        status: 'connected',
        source: 'relay',
        stationName,
        error: null,
      });
    },
    onDevices: applyRelayDevices,
    onStationGone: (stationId) => {
      if (!relayStations.delete(stationId)) return;
      applyBridgeDevices(flattenRelayDevices());
    },
    onReading: recordReading,
    onClose: () => {
      relayStations.clear();
      if (state.source !== 'relay') return;
      bridgeDevices = [];
      setState({
        status: webhidDevice ? 'connected' : 'idle',
        source: webhidDevice ? 'webhid' : null,
        stationName: null,
      });
      publishDevices();
    },
  });
  relay.start();
}

/** Recompute the merged device list and re-resolve the selection. */
function publishDevices(): void {
  const devices = [...bridgeDevices, ...(webhidDevice ? [webhidDevice] : [])];
  if (devices.some((d) => d.connected)) everConnected = true;
  setState({
    devices,
    selectedId: resolveSelection(devices, state.selectedId ?? readSelected()),
    // Connected to a station with nothing bound is a real state: the operator
    // needs to bind a scale in the desktop app's Devices tab.
    error:
      devices.length === 0 && state.source === 'bridge'
        ? 'No scale bound on this station'
        : null,
  });
}

/**
 * Every station's relayed scales as one list. With more than one station the
 * station's name leads each label, so two "Bench scale"s can be told apart.
 * @returns The merged list
 */
function flattenRelayDevices(): ScaleDevice[] {
  const many = relayStations.size > 1;
  return [...relayStations.values()].flatMap(({ label, devices }) =>
    devices.map((d) => ({
      id: d.id,
      label: many && label ? `${label} · ${d.label}` : d.label,
      connected: d.connected,
    })),
  );
}

/**
 * Adopt one station's scales, as relayed.
 * @param devices - Every scale that station is offering.
 * @param station - Which station sent them (older relays don't say).
 */
function applyRelayDevices(
  devices: ScaleDevice[],
  station?: { id: string; label?: string },
): void {
  relayStations.set(station?.id ?? 'station', {
    label: station?.label,
    devices,
  });
  applyBridgeDevices(flattenRelayDevices());
}

/**
 * Adopt the station's advertised scales.
 * @param devices - Every scale the station is offering.
 */
function applyBridgeDevices(devices: ScaleDevice[]): void {
  bridgeDevices = devices;
  publishDevices();
}

/**
 * Tell the local station a print job is waiting.
 *
 * The station polls the API for print jobs on an interval, so a label enqueued
 * just after a poll waits out the whole cycle before it prints. This rides the
 * socket that is already open for scale data and turns that into roughly a
 * fifth of a second.
 *
 * Silently does nothing when no station is connected — the label still prints
 * on the station's own schedule, which is exactly the behaviour without this.
 * @returns Whether a station was there to hear it.
 */
export function nudgeStationPrint(): boolean {
  // Open the link if nothing has yet — or reopen it if inactivity closed it,
  // since enqueueing a label is unambiguously somebody working. Weight fields
  // normally bring it up, but plenty of printing happens on screens that have
  // no scale on them (carcass tags, killsheets) and those deserve the same
  // latency. Idempotent, so the common case costs nothing.
  markActivity();
  const message = { v: 2, type: 'nudge-print' };
  // Loopback first: same machine, no per-message cost. The relay is the only
  // route from a tablet, and a label enqueued there deserves the same latency.
  return (bridge?.send(message) ?? false) || (relay?.send(message) ?? false);
}

/**
 * Look for scales again now: reopen the station link if it went idle, and
 * retry a station that wasn't there before rather than waiting out the
 * backoff (up to five minutes). Called when a scale menu opens, so a scale or
 * station that has come up since the page loaded appears without a reload.
 */
export function refreshScales(): void {
  markActivity();
  startBridge();
  bridge?.retryNow();
  relay?.retryNow();
}

/**
 * Choose which scale weight fields read from.
 * @param deviceId - The scale to select.
 */
export function selectScale(deviceId: string | null): void {
  try {
    if (deviceId) localStorage.setItem(SELECTED_KEY, deviceId);
    else localStorage.removeItem(SELECTED_KEY);
  } catch {
    // The choice just won't survive a reload.
  }
  setState({ selectedId: deviceId });
}

/**
 * Drop the station connection until the operator comes back.
 *
 * Closing the socket is the point rather than a side effect: the station counts
 * attached browsers to decide how fast to poll for print jobs, so letting go is
 * what tells it this bench is idle.
 */
function suspendBridge(): void {
  if (!bridge && !relay) return;
  if (suspended) return;
  suspended = true;
  bridge?.stop();
  bridge = null;
  // Closing this is the point, not a side effect: the station counts
  // subscribers to decide whether to publish at all, so letting go is what
  // stops it paying to stream weights nobody is reading.
  stopRelay();
  bridgeDevices = [];
  setState({
    status: webhidDevice ? 'connected' : 'idle',
    source: webhidDevice ? 'webhid' : null,
    stationName: null,
    error: null,
    suspended: true,
  });
  publishDevices();
}

/**
 * Restart the inactivity countdown, reconnecting first if we had let go.
 *
 * Called from real signals only — pointer, keyboard, visibility, focus, and a
 * changed* scale reading. Changed matters: a serial scale streams constantly,
 * and counting every frame would mean a station never went idle at all.
 */
function markActivity(): void {
  if (suspended) {
    suspended = false;
    setState({ suspended: false });
    startBridge();
  }
  armSuspend();
}

/** Arm the inactivity countdown for the page's current state. */
function armSuspend(): void {
  if (typeof document === 'undefined') return;
  if (suspendTimer) clearTimeout(suspendTimer);
  suspendTimer = setTimeout(
    suspendBridge,
    bridgeSuspendDelay({
      hidden: document.hidden,
      focused: document.hasFocus(),
    }),
  );
}

/** True once the station bridge is serving us — WebHID must then stand down. */
function bridgeIsLive(): boolean {
  return state.source === 'bridge';
}

/** Drop the relay when a faster, cheaper path takes over. */
function stopRelay(): void {
  if (!relay) return;
  relay.stop();
  relay = null;
}

/**
 * Start listening for a local Farmshare Desktop station.
 *
 * Runs on first mount and keeps retrying quietly in the background, so a
 * station that launches the desktop app after the page is already open starts
 * working without a reload.
 */
function startBridge(): void {
  if (!bridgeSupported) return;
  // Coming back from a suspend, or starting for the first time — either way the
  // inactivity countdown should be running from now.
  suspended = false;
  armSuspend();
  // The relay is the tier below loopback; dialling it is a no-op while a
  // station is on loopback or a scale is attached here.
  maybeStartRelay();
  if (bridge) return;

  bridge = new BridgeTransport({
    onOpen: (stationName, devices) => {
      // Release only a device this page opened on its own initiative — that's
      // most likely the one the station is about to want. A scale the operator
      // deliberately attached here stays attached.
      // Loopback beats the relay on every axis, so hand over immediately.
      stopRelay();
      if (!webhidWanted) void detach();
      setState({
        status: 'connected',
        source: 'bridge',
        stationName,
        error: null,
      });
      applyBridgeDevices(devices);
    },
    onDevices: applyBridgeDevices,
    onReading: recordReading,
    onClose: () => {
      if (!bridgeIsLive()) {
        // No station on this machine — a tablet's normal state. Fall through
        // to the relay.
        maybeStartRelay();
        return;
      }
      bridgeDevices = [];
      setState({
        status: webhidDevice ? 'connected' : 'idle',
        source: webhidDevice ? 'webhid' : null,
        stationName: null,
        error: null,
      });
      publishDevices();
      // The station went away — the browser may be able to take the scale
      // directly now.
      void restoreScale();
    },
  });
  bridge.start();
}

/**
 * Reopen a previously granted scale without prompting.
 *
 * WebHID permission grants survive reloads, so after the operator pairs a scale
 * once, every later visit reconnects silently. Safe to call repeatedly.
 */
export async function restoreScale(): Promise<void> {
  // Always worth starting: it's the only transport that works on a station
  // running the desktop app, and it no-ops if already running.
  startBridge();
  if (!hidSupported || device || bridgeIsLive()) return;
  // Coalesce concurrent callers — every weight field on screen calls this on
  // mount, and React strict-mode double-mounts each of them.
  if (restorePromise) {
    await restorePromise;
    return;
  }

  restorePromise = (async () => {
    try {
      const granted = await navigator.hid!.getDevices();
      // The bridge may have come up while we were awaiting permission state.
      if (bridgeIsLive()) return;
      const scales = granted.filter(isScaleDevice);
      if (scales.length === 0) return;

      let remembered: string | null = null;
      try {
        remembered = localStorage.getItem(STORAGE_KEY);
      } catch {
        // Ignore — falls through to the first available scale.
      }
      const match =
        scales.find((d) => deviceKey(d) === remembered) ?? scales[0];
      await attach(match);
    } catch (err) {
      setState({ status: 'error', error: describeError(err) });
    } finally {
      restorePromise = null;
    }
  })();

  await restorePromise;
}

/**
 * Prompt the operator to pick a scale.
 *
 * Must be called from a user gesture — Chromium rejects `requestDevice`
 * otherwise. An empty result means the operator dismissed the picker, which is
 * a cancel, not an error.
 */
export async function connectScale(): Promise<void> {
  // Allowed even with a station serving us: the operator may be attaching a
  // scale the station hasn't bound. Opening one the station already holds fails
  // at the OS level, and that error is worth showing rather than pre-empting.
  if (!hidSupported) return;
  setState({ status: 'connecting', error: null });
  try {
    const picked = await navigator.hid!.requestDevice({
      filters: SCALE_DEVICE_FILTERS,
    });
    if (picked.length === 0) {
      // Dismissing the picker is a cancel, not an error.
      setState({ status: device || bridgeIsLive() ? 'connected' : 'idle' });
      return;
    }
    webhidWanted = {
      vendorId: picked[0].vendorId,
      productId: picked[0].productId,
    };
    await attach(picked[0]);
    // Selecting it is the point. Nothing is auto-selected here — but reaching
    // for the picker and choosing a device *is* the explicit choice, and making
    // the operator then pick the same scale again from a dropdown would be a
    // second answer to a question they just answered.
    selectScale(WEBHID_DEVICE_ID);
  } catch (err) {
    setState({ status: 'error', error: describeError(err) });
  }
}

/** Disconnect and forget the scale, so the next connect re-prompts. */
export async function disconnectScale(): Promise<void> {
  const current = device;
  await detach();
  try {
    await current?.forget?.();
  } catch {
    // `forget` is missing on older Chromium; closing is enough.
  }
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do — worst case we auto-reconnect once more.
  }
  webhidWanted = null;
  if (!bridgeIsLive()) everConnected = false;
  const readings = { ...state.readings };
  delete readings[WEBHID_DEVICE_ID];
  setState({
    // A station may still be serving other scales; only this browser's own
    // device went away.
    status: bridgeIsLive() ? 'connected' : 'idle',
    readings,
    error: null,
    source: bridgeIsLive() ? 'bridge' : null,
  });
  publishDevices();
}

// Activity signals. These decide whether this browser still counts as a bench
// somebody is working at — which the station reads, via the client count, to
// choose how fast to poll for print jobs.
if (typeof document !== 'undefined') {
  // `pointerdown`/`keydown` rather than move/scroll: a mouse drifting under a
  // sleeve isn't someone working, and passive listeners on move would fire
  // thousands of times a shift for no benefit.
  const activity = () => markActivity();
  document.addEventListener('pointerdown', activity, { passive: true });
  document.addEventListener('keydown', activity, { passive: true });
  window.addEventListener('focus', activity);

  // Re-arm rather than suspend outright: the delay depends on the new state,
  // and losing focus is not the same as being gone.
  window.addEventListener('blur', () => armSuspend());
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) armSuspend();
    else markActivity();
  });
}

// Hot-plug. Unplugging the scale mid-shift should show as disconnected, and
// plugging it back in should recover on its own without a page reload.
if (hidSupported) {
  navigator.hid!.addEventListener('disconnect', (event) => {
    if (device !== event.device) return;
    // `webhidWanted` deliberately survives this: a scale that powered down on
    // standby should come back by itself when it powers up.
    detach().then(() => {
      setState({
        status: bridgeIsLive() ? 'connected' : 'idle',
        error: webhidWanted
          ? 'Scale went away — waiting for it to come back'
          : 'Scale was unplugged',
        source: bridgeIsLive() ? 'bridge' : null,
      });
      publishDevices();
    });
  });

  navigator.hid!.addEventListener('connect', (event) => {
    if (device) return;

    // Reattach a scale the operator asked for, even with a station serving
    // us — they chose this one, and it just came back from standby.
    const wanted =
      !!webhidWanted &&
      event.device.vendorId === webhidWanted.vendorId &&
      event.device.productId === webhidWanted.productId;

    // Otherwise only adopt a scale when nothing else is: on a bridge station
    // the device almost certainly belongs to the station.
    if (!wanted && (bridgeIsLive() || !isScaleDevice(event.device))) return;

    attach(event.device).catch((err) => {
      setState({ status: 'error', error: describeError(err) });
    });
  });
}
